import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { Plus, Upload, Search, Archive, Eye, Package, Loader2 } from "lucide-react";

interface ProductRow {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly productType: string;
  readonly status: string;
  readonly price: number;
  readonly compareAtPrice: number | null;
  readonly hasVariants: boolean;
  readonly thumbnail: string | null;
  readonly totalStock: number;
  readonly createdAt: string;
}

interface ProductListResponse {
  readonly success: boolean;
  readonly data: readonly ProductRow[];
  readonly meta: { readonly total: number; readonly page: number; readonly limit: number };
}

const STATUS_MAP: Record<string, { label: string; bg: string; color: string }> = {
  draft: {
    label: "טיוטה",
    bg: "rgba(245,158,11,0.12)",
    color: "#B45309",
  },
  active: {
    label: "פעיל",
    bg: "rgba(16,185,129,0.12)",
    color: "#065F46",
  },
  archived: {
    label: "בארכיון",
    bg: "rgba(100,116,139,0.12)",
    color: "#475569",
  },
};

const TYPE_MAP: Record<string, string> = {
  physical: "פיזי",
  digital: "דיגיטלי",
  service: "שירות",
};

function formatNIS(agorot: number): string {
  return `₪${(agorot / 100).toFixed(2)}`;
}

function StatusBadge({ status }: { readonly status: string }): ReactNode {
  const s = STATUS_MAP[status] ?? STATUS_MAP["draft"]!;
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        borderRadius: "20px",
        padding: "2px 10px",
        fontSize: "0.73rem",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "כל הסטטוסים" },
  { value: "draft", label: "טיוטה" },
  { value: "active", label: "פעיל" },
  { value: "archived", label: "בארכיון" },
];

export function ProductListPage(): ReactNode {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [products, setProducts] = useState<readonly ProductRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const limit = 20;

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      params.set("sort", "created_at");
      params.set("order", "desc");

      const res = await fetch(`/api/products?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = (await res.json()) as ProductListResponse;
      if (json.success) {
        setProducts(json.data);
        setTotal(json.meta.total);
      }
    } finally {
      setLoading(false);
    }
  }, [accessToken, page, search, statusFilter]);

  useEffect(() => { void fetchProducts(); }, [fetchProducts]);

  const handleBulkAction = useCallback(
    async (action: "archive" | "activate") => {
      if (selected.size === 0) return;
      await fetch("/api/products/bulk-action", {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ids: [...selected], action }),
      });
      setSelected(new Set());
      void fetchProducts();
    },
    [accessToken, selected, fetchProducts],
  );

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const totalPages = Math.ceil(total / limit);

  return (
    <div style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "0.75rem" }}>
        <div>
          <h1
            className="font-display text-gradient"
            style={{ fontWeight: 900, fontSize: "1.7rem", lineHeight: 1.2, margin: 0 }}
          >
            מוצרים
          </h1>
          {total > 0 && (
            <p style={{ fontSize: "0.85rem", color: "hsl(var(--muted-foreground))", marginTop: "0.2rem" }}>
              {total} מוצרים סה"כ
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            onClick={() => navigate("/products/import")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
              padding: "0 1rem",
              height: "40px",
              borderRadius: "12px",
              border: "1.5px solid hsl(var(--border))",
              background: "hsl(var(--card))",
              color: "hsl(var(--foreground))",
              fontSize: "0.875rem",
              fontWeight: 600,
              cursor: "pointer",
              transition: "border-color 0.15s, box-shadow 0.15s",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--brand-primary)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "hsl(var(--border))"; }}
          >
            <Upload size={15} />
            ייבוא
          </button>
          <button
            onClick={() => navigate("/products/new")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
              padding: "0 1.1rem",
              height: "40px",
              borderRadius: "12px",
              border: "none",
              background: "var(--brand-gradient)",
              color: "#fff",
              fontSize: "0.875rem",
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "var(--shadow-brand)",
              transition: "opacity 0.15s, transform 0.15s",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "0.9"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "1"; }}
          >
            <Plus size={16} />
            מוצר חדש +
          </button>
        </div>
      </div>

      {/* ── Search + Filter ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.625rem", alignItems: "center" }}>
        <div style={{ position: "relative", flex: "1 1 240px" }}>
          <Search
            size={16}
            style={{
              position: "absolute",
              insetInlineStart: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "hsl(var(--muted-foreground))",
            }}
          />
          <input
            placeholder="חיפוש מוצרים..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{
              width: "100%",
              height: "42px",
              paddingInlineStart: "38px",
              paddingInlineEnd: "12px",
              borderRadius: "12px",
              border: "1.5px solid hsl(var(--border))",
              background: "hsl(var(--card))",
              color: "hsl(var(--foreground))",
              fontSize: "0.875rem",
              outline: "none",
              transition: "border-color 0.15s, box-shadow 0.15s",
              boxSizing: "border-box",
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "var(--brand-primary)";
              e.currentTarget.style.boxShadow = "0 0 0 3px rgba(124,58,237,0.1)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "hsl(var(--border))";
              e.currentTarget.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Status filter pills */}
        <div style={{ display: "flex", gap: "0.375rem", flexWrap: "wrap" }}>
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setStatusFilter(opt.value); setPage(1); }}
              style={{
                height: "42px",
                padding: "0 1rem",
                borderRadius: "12px",
                border: "1.5px solid",
                borderColor: statusFilter === opt.value ? "var(--brand-primary)" : "hsl(var(--border))",
                background: statusFilter === opt.value ? "var(--brand-primary-light)" : "hsl(var(--card))",
                color: statusFilter === opt.value ? "var(--brand-primary)" : "hsl(var(--muted-foreground))",
                fontSize: "0.8rem",
                fontWeight: statusFilter === opt.value ? 700 : 500,
                cursor: "pointer",
                transition: "all 0.15s",
                whiteSpace: "nowrap",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Bulk actions ── */}
      {selected.size > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            padding: "0.6rem 1rem",
            borderRadius: "12px",
            background: "var(--brand-primary-light)",
            border: "1.5px solid rgba(124,58,237,0.2)",
            fontSize: "0.875rem",
          }}
        >
          <span style={{ fontWeight: 700, color: "var(--brand-primary)" }}>{selected.size} נבחרו</span>
          <div style={{ width: "1px", height: "16px", background: "hsl(var(--border))" }} />
          <button
            onClick={() => void handleBulkAction("archive")}
            style={{
              display: "inline-flex", alignItems: "center", gap: "0.3rem",
              padding: "0 0.75rem", height: "32px", borderRadius: "8px",
              border: "1.5px solid hsl(var(--border))", background: "hsl(var(--card))",
              color: "hsl(var(--foreground))", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer",
            }}
          >
            <Archive size={13} /> ארכיון
          </button>
          <button
            onClick={() => void handleBulkAction("activate")}
            style={{
              display: "inline-flex", alignItems: "center", gap: "0.3rem",
              padding: "0 0.75rem", height: "32px", borderRadius: "8px",
              border: "1.5px solid hsl(var(--border))", background: "hsl(var(--card))",
              color: "hsl(var(--foreground))", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer",
            }}
          >
            <Eye size={13} /> הפעלה
          </button>
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "4rem 0" }}>
          <Loader2
            className="animate-spin"
            size={32}
            style={{ color: "var(--brand-primary)" }}
          />
        </div>
      ) : products.length === 0 ? (
        /* Empty state */
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "4rem 1rem",
            borderRadius: "var(--radius-lg)",
            border: "2px dashed hsl(var(--border))",
            background: "hsl(var(--card))",
            gap: "1rem",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "64px", height: "64px", borderRadius: "50%",
              background: "var(--brand-primary-light)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Package size={28} style={{ color: "var(--brand-primary)" }} />
          </div>
          <div>
            <p style={{ fontWeight: 700, fontSize: "1rem", margin: 0 }}>אין מוצרים עדיין</p>
            <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", marginTop: "0.25rem" }}>
              הוסף את המוצר הראשון שלך
            </p>
          </div>
          <button
            onClick={() => navigate("/products/new")}
            style={{
              display: "inline-flex", alignItems: "center", gap: "0.4rem",
              padding: "0 1.25rem", height: "42px", borderRadius: "12px",
              border: "none", background: "var(--brand-gradient)",
              color: "#fff", fontWeight: 700, fontSize: "0.875rem",
              cursor: "pointer", boxShadow: "var(--shadow-brand)",
            }}
          >
            <Plus size={16} /> הוספת מוצר ראשון
          </button>
        </div>
      ) : (
        <>
          {/* ── Desktop table ── */}
          <div
            className="hidden md:block"
            style={{
              borderRadius: "var(--radius-lg)",
              border: "1.5px solid hsl(var(--border))",
              background: "hsl(var(--card))",
              boxShadow: "var(--shadow-card)",
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
              <thead>
                <tr style={{ borderBottom: "1.5px solid hsl(var(--border))", background: "hsl(var(--muted)/0.4)" }}>
                  <th style={{ padding: "0.75rem 1rem", width: "36px" }}>
                    <input
                      type="checkbox"
                      style={{ borderRadius: "4px", cursor: "pointer" }}
                      checked={selected.size === products.length && products.length > 0}
                      onChange={() => {
                        if (selected.size === products.length) setSelected(new Set());
                        else setSelected(new Set(products.map((p) => p.id)));
                      }}
                    />
                  </th>
                  <th style={{ padding: "0.75rem 1rem", width: "52px", textAlign: "start", fontSize: "0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}>תמונה</th>
                  <th style={{ padding: "0.75rem 1rem", textAlign: "start", fontSize: "0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}>שם</th>
                  <th style={{ padding: "0.75rem 1rem", textAlign: "start", fontSize: "0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}>סטטוס</th>
                  <th style={{ padding: "0.75rem 1rem", textAlign: "start", fontSize: "0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}>סוג</th>
                  <th style={{ padding: "0.75rem 1rem", textAlign: "end", fontSize: "0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}>מחיר</th>
                  <th style={{ padding: "0.75rem 1rem", textAlign: "end", fontSize: "0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}>מלאי</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, i) => (
                  <ProductTableRow
                    key={p.id}
                    product={p}
                    isSelected={selected.has(p.id)}
                    isLast={i === products.length - 1}
                    onToggle={toggleSelect}
                    onNavigate={(id) => navigate(`/products/${id}`)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Mobile cards ── */}
          <div className="md:hidden" style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {products.map((p) => (
              <ProductMobileCard
                key={p.id}
                product={p}
                isSelected={selected.has(p.id)}
                onToggle={toggleSelect}
                onNavigate={(id) => navigate(`/products/${id}`)}
              />
            ))}
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", paddingTop: "0.5rem" }}>
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                הקודם
              </Button>
              <span style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", padding: "0 0.5rem" }}>
                {page} / {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                הבא
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────────────────── */

interface ProductTableRowProps {
  readonly product: ProductRow;
  readonly isSelected: boolean;
  readonly isLast: boolean;
  readonly onToggle: (id: string) => void;
  readonly onNavigate: (id: string) => void;
}

function ProductTableRow({ product: p, isSelected, isLast, onToggle, onNavigate }: ProductTableRowProps): ReactNode {
  const [hovered, setHovered] = useState(false);

  return (
    <tr
      style={{
        borderBottom: isLast ? "none" : "1px solid hsl(var(--border))",
        background: hovered ? "linear-gradient(90deg, rgba(124,58,237,0.04) 0%, rgba(236,72,153,0.02) 100%)" : "transparent",
        cursor: "pointer",
        transition: "background 0.15s",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onNavigate(p.id)}
    >
      <td style={{ padding: "0.75rem 1rem" }} onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          style={{ borderRadius: "4px", cursor: "pointer" }}
          checked={isSelected}
          onChange={() => onToggle(p.id)}
        />
      </td>
      <td style={{ padding: "0.75rem 1rem" }}>
        {p.thumbnail ? (
          <img
            src={p.thumbnail}
            alt={p.name}
            style={{ width: "40px", height: "40px", borderRadius: "10px", objectFit: "cover", border: "1.5px solid hsl(var(--border))" }}
          />
        ) : (
          <div
            style={{
              width: "40px", height: "40px", borderRadius: "10px",
              background: "var(--brand-primary-light)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Package size={16} style={{ color: "var(--brand-primary)" }} />
          </div>
        )}
      </td>
      <td style={{ padding: "0.75rem 1rem", fontWeight: 600 }}>{p.name}</td>
      <td style={{ padding: "0.75rem 1rem" }}>
        <StatusBadge status={p.status} />
      </td>
      <td style={{ padding: "0.75rem 1rem", color: "hsl(var(--muted-foreground))", fontSize: "0.8rem" }}>
        {TYPE_MAP[p.productType] ?? p.productType}
      </td>
      <td style={{ padding: "0.75rem 1rem", textAlign: "end", fontFamily: "monospace", fontWeight: 600 }}>
        {formatNIS(p.price)}
      </td>
      <td style={{ padding: "0.75rem 1rem", textAlign: "end" }}>
        <span
          style={{
            fontWeight: 600,
            color: p.totalStock === 0
              ? "var(--danger)"
              : p.totalStock <= 5
              ? "var(--warning)"
              : "inherit",
          }}
        >
          {p.totalStock}
        </span>
      </td>
    </tr>
  );
}

interface ProductMobileCardProps {
  readonly product: ProductRow;
  readonly isSelected: boolean;
  readonly onToggle: (id: string) => void;
  readonly onNavigate: (id: string) => void;
}

function ProductMobileCard({ product: p, isSelected, onToggle, onNavigate }: ProductMobileCardProps): ReactNode {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="card-premium"
      style={{
        background: "hsl(var(--card))",
        borderRadius: "var(--radius-md)",
        border: `1.5px solid ${hovered ? "var(--brand-primary)" : "hsl(var(--border))"}`,
        boxShadow: hovered ? "var(--shadow-card-hover)" : "var(--shadow-card)",
        transform: hovered ? "translateY(-2px)" : "none",
        transition: "all 0.2s ease",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.75rem",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onNavigate(p.id)}
    >
      <input
        type="checkbox"
        style={{ borderRadius: "4px", cursor: "pointer", flexShrink: 0 }}
        checked={isSelected}
        onChange={(e) => { e.stopPropagation(); onToggle(p.id); }}
        onClick={(e) => e.stopPropagation()}
      />
      {p.thumbnail ? (
        <img
          src={p.thumbnail}
          alt={p.name}
          style={{ width: "48px", height: "48px", borderRadius: "12px", objectFit: "cover", flexShrink: 0, border: "1.5px solid hsl(var(--border))" }}
        />
      ) : (
        <div
          style={{
            width: "48px", height: "48px", borderRadius: "12px",
            background: "var(--brand-primary-light)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}
        >
          <Package size={20} style={{ color: "var(--brand-primary)" }} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontWeight: 700, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</p>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.375rem", flexWrap: "wrap" }}>
          <StatusBadge status={p.status} />
          <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.875rem" }}>{formatNIS(p.price)}</span>
          <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>מלאי: {p.totalStock}</span>
        </div>
      </div>
    </div>
  );
}
